/**
 * Offline queue for voice utterances.
 *
 * T132 MVP: when the transcribe POST fails because of a network blip
 * (stadium WiFi flake, mobile data outage), store the audio blob and
 * its metadata in localStorage instead of dropping the utterance.
 * When the browser comes back online, flush the queue against the
 * same endpoint.
 *
 * Limits: localStorage caps at ~5 MB. Audio blobs are typically
 * 30–100 KB at 16 kHz mono, so ~50 utterances fit comfortably. We
 * cap the queue at 50 entries and shed the oldest if a 51st arrives.
 *
 * IndexedDB would be the proper home for blob storage but adds
 * Dexie/idb-keyval as a dependency and async-init complexity. For
 * the MVP and the size envelope above, localStorage + base64 is
 * the simpler call.
 *
 * Storage shape:
 *   key   = "zcd_voice_offline_queue"
 *   value = JSON array of { id, b64, ext, match_id, ts }
 */

const KEY = "zcd_voice_offline_queue";
const MAX_ENTRIES = 50;


function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      // FileReader gives "data:audio/webm;base64,XXXX..." — strip the
      // prefix so we store only the payload.
      const dataUrl = reader.result || "";
      const comma = dataUrl.indexOf(",");
      resolve(comma >= 0 ? dataUrl.slice(comma + 1) : "");
    };
    reader.onerror = () => reject(reader.error || new Error("blob read failed"));
    reader.readAsDataURL(blob);
  });
}


function base64ToBlob(b64, type) {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type });
}


function readQueue() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}


function writeQueue(arr) {
  try {
    localStorage.setItem(KEY, JSON.stringify(arr));
  } catch (e) {
    // Likely QuotaExceeded — drop oldest half and retry once.
    try {
      localStorage.setItem(KEY, JSON.stringify(arr.slice(-Math.floor(arr.length / 2))));
    } catch { /* still failing — give up silently */ }
  }
}


export async function enqueue({ blob, ext, matchId }) {
  if (!blob) return null;
  const b64 = await blobToBase64(blob);
  const entry = {
    id: `vo_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    b64,
    ext: ext || "webm",
    type: blob.type || "audio/webm",
    match_id: matchId || null,
    ts: Date.now(),
  };
  const arr = readQueue();
  arr.push(entry);
  // Cap at MAX_ENTRIES — shed oldest to make room.
  while (arr.length > MAX_ENTRIES) arr.shift();
  writeQueue(arr);
  return entry.id;
}


export function peekCount() {
  return readQueue().length;
}


// Flush all queued items through the provided sender. Sender is a
// function (blob, ext, matchId) => Promise that resolves on a
// successful POST and rejects on a network/server error. Items that
// succeed are removed from the queue; items that fail stay queued
// for the next flush attempt. Returns {sent, remaining}.
export async function flushAll(sender) {
  if (!navigator.onLine) return { sent: 0, remaining: peekCount() };
  if (typeof sender !== "function") return { sent: 0, remaining: peekCount() };
  let arr = readQueue();
  if (arr.length === 0) return { sent: 0, remaining: 0 };
  let sent = 0;
  const survivors = [];
  for (const entry of arr) {
    try {
      const blob = base64ToBlob(entry.b64, entry.type);
      await sender(blob, entry.ext, entry.match_id);
      sent += 1;
    } catch {
      // Network still bad or server still 5xx — keep for next try.
      survivors.push(entry);
    }
  }
  writeQueue(survivors);
  return { sent, remaining: survivors.length };
}


export function clearAll() {
  try { localStorage.removeItem(KEY); } catch { /* ignore */ }
}
